import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Single, centralized scroll behavior for the whole app (mounted once inside the Router).
 * - Navigating to a new path with NO hash → jump to the TOP (never inherit previous scroll).
 * - Navigating with a hash (e.g. "/#employers") → wait for the target element to mount, then
 *   scroll to it accounting for the sticky header height. Works from any route and when already
 *   on the destination page. Retries briefly because homepage sections mount after data loads.
 */
const HEADER_OFFSET = 72; // sticky public header height + small gap

export default function ScrollManager() {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let tries = 0;
      let timer;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
          window.scrollTo({ top: y < 0 ? 0 : y, behavior: reduced ? 'auto' : 'smooth' });
        } else if (tries++ < 40) {
          timer = setTimeout(tryScroll, 80);
        }
      };
      tryScroll();
      return () => timer && clearTimeout(timer);
    }
    // No hash → always start at the top of the destination page.
    window.scrollTo(0, 0);
  }, [pathname, hash, key]);

  return null;
}
